import {useTheme} from '@mui/material';
import {OdontoBookingEnum} from 'presentation/constants';
import {useBeneficiaryCard, usePersonalData} from 'presentation/hooks';
import {getOdontoStepPosition} from 'presentation/modules/booking/odonto/utils/getOdontoStepPosition';
import {FlowEnum} from 'presentation/modules/booking/shared/constants/flowEnum';
import {useBookingTheme} from 'presentation/modules/booking/shared/hooks/useBookingTheme';
import {useResetBookingFlow} from 'presentation/modules/booking/shared/hooks/useResetBookingFlow';
import {useBookingFlowStepsStore} from 'presentation/modules/booking/shared/store/useBookingFlowStepsStore';
import {useBookingStore} from 'presentation/modules/booking/shared/store/useBookingStore';
import {ChangeFlowVariantEnum} from 'presentation/modules/booking/shared/ui/components/ChangeFlowDialog/constants';
import {translations} from 'presentation/translations-new';
import {formatText, getUserInfoFromToken} from 'presentation/utils';
import {useNavigate} from 'react-router-dom';
import {UseOdontoSummarySectionGroupProps} from './types';
import {useSummaryChangeDrawerStore} from 'presentation/modules/booking/shared/hooks/useBookingSummary/store/useSummaryChangeDrawerStore';

const {usingYourLocation} = translations['pt-br'].commons;

export function useOdontoSummarySectionGroup({
	activeStep,
	stepLabels,
	handleChangeModals,
}: UseOdontoSummarySectionGroupProps) {
	const bookingData = useBookingStore(state => state.bookingData);
	const {beneficiaryCardPlanData} = useBeneficiaryCard();
	const {currentEmail, currentPhone} = usePersonalData();
	const bookingTheme = useBookingTheme({flowType: FlowEnum.ODONTO});
	const activeBookingFlowStep = useBookingFlowStepsStore(state => state.bookingStep);
	const setActiveBookingFlowStep = useBookingFlowStepsStore(state => state.setBookingStep);
	const {colors} = useTheme();
	const navigate = useNavigate();
	const {resetBookingFlow} = useResetBookingFlow();
	const setOpenDrawerState = useSummaryChangeDrawerStore(state => state.setSummaryChangeDrawer);

	const {
		isRebooking,
		unitName,
		unitAddress,
		specialtyCode,
		specialtyName,
		isManualLocation,
		districtName,
		odontoRecommendations,
	} = bookingData;

	const isLastStep = activeStep === stepLabels.length - 1;
	const isUsingGeolocation = !isManualLocation;

	const {cdUsuario} = getUserInfoFromToken();
	const {UNIT_STEP, SPECIALITY_OR_EXAM_STEP, LOCATION_STEP} = getOdontoStepPosition({
		isManualLocation,
	});

	const showSpecialtyField = activeStep > 1 && !isLastStep;
	const showUnitField = !isLastStep && (isManualLocation ? activeStep > 3 : activeStep > 2);
	const showDistricsField = isManualLocation && activeStep === UNIT_STEP && districtName;
	const showOdontoRecommendations = odontoRecommendations && isLastStep;

	const getLocationPrimaryText = () => {
		if (isUsingGeolocation) {
			return usingYourLocation;
		}

		return formatText(bookingData.cityName);
	};

	const getLocationSecondaryText = () => {
		if (isUsingGeolocation) return undefined;

		return formatText(bookingData.ufName);
	};

	const handleChangeLocation = () => {
		if (activeBookingFlowStep === SPECIALITY_OR_EXAM_STEP) {
			resetBookingFlow();
			setOpenDrawerState(false);

			if (bookingData.isManualLocation) {
				setActiveBookingFlowStep(LOCATION_STEP);
				return;
			}

			navigate(OdontoBookingEnum.ODONTO_BOOKING_UF, {replace: true});
			setActiveBookingFlowStep(LOCATION_STEP);
			return;
		}

		handleChangeModals(ChangeFlowVariantEnum.LOCATION);
	};

	return {
		showSpecialtyField,
		showUnitField,
		getLocationPrimaryText,
		getLocationSecondaryText,
		showDistricsField,
		bookingTheme,
		colors,
		cdUsuario,
		beneficiaryCardPlanData,
		currentEmail,
		currentPhone,
		isLastStep,
		isUsingGeolocation,
		isRebooking,
		unitName,
		unitAddress,
		specialtyCode,
		specialtyName,
		handleChangeLocation,
		odontoRecommendations,
		showOdontoRecommendations,
	};
}
